import { api, type Tournament } from "../api";
import { MemberAvatar } from "../avatar";
import { useLoad } from "../useLoad";

interface Props {
  teamId: string;
  tournament: Tournament;
  revision: number;
  onError: (message: string) => void;
}

/**
 * The running prediction leaderboard. Task 11.7, requirement 10.1.
 *
 * Two properties of the scoring show up here and are worth stating, because they are the reason the
 * board stays interesting to a child:
 *
 * Every round is worth the same total (9.7) — the points doubling exactly cancels the field halving — so
 * **nobody is ever mathematically eliminated**, and the final is worth as much as the whole opening
 * round. That is why this shows the remaining points rather than only the standings.
 *
 * The champion bonus is one round's worth (9.8), meaningful but not decisive. A single lucky guess
 * cannot beat somebody who was right all day, which 9.8.1 records as a correction to an earlier
 * formulation that would have let it.
 */
export function Leaderboard({ teamId, tournament, revision, onError }: Props) {
  const { data, loading } = useLoad(
    () => api.leaderboard(teamId, tournament.tournamentId),
    [teamId, tournament.tournamentId, revision],
    onError,
  );

  const lines = data?.lines ?? [];
  if (lines.length === 0) {
    return (
      <div className="card">
        <p className="section-title">Leaderboard</p>
        <p className="muted" style={{ margin: 0 }}>
          {loading ? "Loading…" : "Nobody has predicted yet."}
        </p>
      </div>
    );
  }

  const awarded = Math.max(...lines.map((line) => line.points), 0);
  const remaining = (data?.maximumPoints ?? 0) - awarded;

  return (
    <div className="card">
      <p className="section-title">Leaderboard</p>
      <ul className="list">
        {lines.map((line, index) => (
          <li key={line.sub} className="row">
            <span className={`place${index === 0 && line.points > 0 ? " won" : ""}`}>
              {index + 1}
            </span>
            <MemberAvatar sub={line.sub} picture={line.picture} name={line.name} />
            <span className="grow">
              {/* Requirement 9.9 bans text on *picks*, not on a leaderboard — a list of people has to
                  name them. The avatar sits alongside so the same person is recognisable here and on
                  the television, where only the mark is allowed. */}
              <span className="name">{line.name ?? "A member"}</span>
              <span className="note">
                {line.correctPicks} right
                {line.championBonus > 0 && ` · champion +${line.championBonus}`}
                {line.scoredPicks > 0 && (
                  <>
                    {" · "}
                    {Math.round(line.accuracy * 100)}% accurate
                    {/* Requirement 10.5 — shown and flagged, never withheld. Telling a grandparent
                        they do not appear yet is a worse outcome than a noisy number with an asterisk. */}
                    {line.provisional && <span className="muted"> (early days)</span>}
                  </>
                )}
              </span>
            </span>
            <span className="points">{line.points}</span>
          </li>
        ))}
      </ul>
      {remaining > 0 && (
        <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
          {remaining} still to play for — every round is worth the same, so nobody is out of it.
        </p>
      )}
    </div>
  );
}
